// Compare two or more models: migrations, current models and sketches. Two models can be shown as
// one diagram with the differences colored (overlay) or next to each other with linked panning and
// zoom; any number can be lined up in a matrix of entities.
import { lintModel, validateDocument, type ModelSpec } from '@modelviewer/core';
import type { Viewport } from '@xyflow/react';
import type { DockviewPanelApi } from 'dockview-react';
import { ArrowLeftRight, Loader2, PanelRightClose, PanelRightOpen, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Canvas, Divider, ToolButton, type CanvasSelection, type CanvasTarget } from '../diagram/Canvas';
import { useCardFonts } from '../diagram/DiagramView';
import type { Graph, GraphOptions } from '../diagram/graph';
import { measureText } from '../diagram/measure';
import { useDiagramSettings } from '../diagram/settings';
import { layoutInput, useLayout } from '../diagram/useLayout';
import { ChangeList } from '../diff/ChangeList';
import { cn } from '../lib/cn';
import { matches, PHONE } from '../lib/media';
import { modelTitle } from '../state/dock';
import { layoutKeyFor } from '../state/sketches';
import { loadModel, refKey, revealEntity, select, setActiveRef, useWorkspace, type LoadedModel, type ModelRef } from '../state/workspace';
import { compareGraphs, compareMatrix, compareTwo, type CompareSide, type MatrixCell } from './compare';

type Mode = 'overlay' | 'side' | 'matrix';

const LETTERS = 'ABCDEFGH';

function useModels(refs: ModelRef[]): { models: (LoadedModel | undefined)[]; errors: (string | undefined)[] } {
  const keys = refs.map(refKey);
  const models = useWorkspace(useShallow((s) => keys.map((k) => s.models[k])));
  const errors = useWorkspace(useShallow((s) => keys.map((k) => s.modelErrors[k])));
  const joined = keys.join('+');
  useEffect(() => {
    for (const ref of refs) void loadModel(ref);
    // `joined` identifies `refs`, which is a new array on every render.
  }, [joined]);
  return { models, errors };
}

/** Whether `ref` is a sketch started from `base`, so its declared renames apply. */
function branchedFrom(ref: ModelRef, base: ModelRef): boolean {
  if (!ref.sketchId) return false;
  const sketch = useWorkspace.getState().sketches[ref.sketchId];
  const b = sketch?.base;
  return !!b && refKey({ setKey: b.setKey, migrationId: b.migrationId, ...(b.sketchId ? { sketchId: b.sketchId } : {}) }) === refKey(base);
}

function Letter({ index }: { index: number }) {
  return <span className={cn('flex h-4 w-4 flex-none items-center justify-center rounded text-[10px] font-bold', index === 0 ? 'bg-panel text-muted' : 'bg-accent-soft text-accent')}>{LETTERS[index]}</span>;
}

function AddModel({ refs, onAdd }: { refs: ModelRef[]; onAdd: (ref: ModelRef) => void }) {
  const discovery = useWorkspace((s) => s.discovery);
  const sketches = useWorkspace((s) => s.sketches);
  const taken = new Set(refs.map(refKey));
  const options: { value: string; label: string }[] = [];
  for (const set of discovery?.sets ?? []) {
    options.push({ value: JSON.stringify({ setKey: set.key, migrationId: null }), label: `${set.contextName} · current model` });
    for (const m of [...set.migrations].reverse()) if (m.designerPath) options.push({ value: JSON.stringify({ setKey: set.key, migrationId: m.id }), label: `${set.contextName} · ${m.name}` });
  }
  for (const s of Object.values(sketches).sort((a, b) => b.updated - a.updated)) {
    options.push({ value: JSON.stringify({ setKey: s.setKey ?? '', migrationId: null, sketchId: s.id }), label: `Sketch · ${s.doc.title}` });
  }
  return (
    <select
      aria-label="Add a model to the comparison"
      value=""
      onChange={(e) => e.target.value && onAdd(JSON.parse(e.target.value) as ModelRef)}
      className="h-7 max-w-[220px] rounded-md border border-dashed border-border-strong bg-transparent px-1.5 text-[12px] text-muted hover:text-text max-md:max-w-[136px] pointer-coarse:h-9"
    >
      <option value="">+ Add a model…</option>
      {options
        .filter((o) => !taken.has(refKey(JSON.parse(o.value) as ModelRef)))
        .map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
    </select>
  );
}

export function CompareView({ refs, api }: { refs: ModelRef[]; api: DockviewPanelApi }) {
  const { models: loaded, errors: loadErrors } = useModels(refs);
  const settings = useDiagramSettings();
  const source = useWorkspace((s) => s.source);
  const fontsReady = useCardFonts();
  const [mode, setMode] = useState<Mode>(refs.length > 2 ? 'matrix' : 'overlay');
  // On a phone the list of differences would cover most of the diagram; it opens from the toolbar.
  const [showChanges, setShowChanges] = useState(() => !matches(PHONE));
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [active, setActive] = useState(api.isActive);
  useEffect(() => {
    const d = api.onDidActiveChange((e) => setActive(e.isActive));
    return () => d.dispose();
  }, [api]);
  useEffect(() => {
    if (refs.length > 2) setMode('matrix');
  }, [refs.length]);
  // The inspector follows the newer model (a sketch there can be edited while comparing).
  const last = refs.at(-1)!;
  useEffect(() => {
    if (active) setActiveRef(last);
  }, [active, last]);

  const setRefs = (next: ModelRef[]) => {
    api.updateParameters({ refs: next });
    api.setTitle(`Compare ${next.map(modelTitle).join(' · ')}`);
  };

  const ready = loaded.every(Boolean);
  const sides = useMemo<CompareSide[]>(
    () => (ready ? loaded.map((l, i) => ({ model: l!.result.doc.model, renames: i > 0 && branchedFrom(refs[i]!, refs[0]!) ? l!.result.doc.renames : undefined })) : []),
    [ready, loaded, refs],
  );
  const diffs = useMemo(() => sides.map((s, i) => (i === 0 ? null : compareTwo(sides[0]!, s))), [sides]);
  const options = useMemo<GraphOptions>(() => ({ properties: settings.properties, collapseJoins: settings.collapseJoins, measure: measureText }), [settings.properties, settings.collapseJoins]);
  const graphs = useMemo(() => (sides.length === 2 && fontsReady ? compareGraphs(sides[0]!, sides[1]!, diffs[1]!, options) : null), [sides, diffs, fontsReady, options]);
  const input = useMemo(() => (graphs ? layoutInput(graphs.overlay) : null), [graphs]);
  const layoutKey = source ? `${source.id}|compare:${refs.map(refKey).join('+')}|${settings.properties}` : null;
  const seedKeys = source ? refs.slice(0, 2).map((r) => layoutKeyFor(source.id, r, settings.properties)) : [];
  const { positions, save, relayout } = useLayout(layoutKey, input, seedKeys);

  // Node ids are the first model's names; the second model's entities map onto them.
  const toA = useMemo(() => new Map((diffs[1]?.entities ?? []).map((m) => [m.to, m.from])), [diffs]);
  const selection = useWorkspace((s) => s.selection);
  const selectedFor = useCallback(
    (graph: Graph): CanvasSelection => {
      const none = { node: null, row: null, edge: null };
      if (!selection || refs.length !== 2) return none;
      const side = refs.findIndex((r) => refKey(r) === refKey(selection.ref));
      if (side < 0) return none;
      const idOf = (key: string) => (side === 1 ? (toA.get(key) ?? key) : key);
      const exists = (id: string) => graph.nodes.some((n) => n.id === id);
      const v = selection.value;
      if (v.kind === 'entity') return { ...none, node: exists(idOf(v.key)) ? idOf(v.key) : null };
      if (v.kind === 'property') return { ...none, row: `${idOf(v.entity)}::${v.property}` };
      return { ...none, edge: side === 1 ? `rel:${v.index}` : `was:rel:${v.index}` };
    },
    [selection, refs, toA],
  );
  const onSelectIn = useCallback(
    (side: 0 | 1 | 'overlay') => (target: CanvasTarget | null) => {
      const pick = (removed: boolean) => (side === 'overlay' ? (removed ? refs[0]! : refs[1]!) : refs[side]!);
      if (!target) return select(refs[1]!, null);
      if (target.kind === 'node') return select(pick(target.node.diff === 'removed'), { kind: 'entity', key: target.node.key });
      if (target.kind === 'row') {
        const removed = target.row.diff === 'removed' && side === 'overlay';
        return select(pick(removed), { kind: 'property', entity: removed ? (target.node.previousKey ?? target.node.key) : target.node.key, property: target.row.id });
      }
      if (target.edge.relationship !== null) select(pick(target.edge.diff === 'removed'), { kind: 'relationship', index: target.edge.relationship });
    },
    [refs],
  );

  const header = (
    <div className="flex min-h-10 flex-none flex-wrap items-center gap-2 border-b border-border bg-panel px-3 py-1.5">
      {refs.map((ref, i) => (
        <span key={refKey(ref)} className="flex items-center gap-1.5 rounded-md border border-border bg-bg py-0.5 pr-1 pl-1.5 text-[12.5px]">
          <Letter index={i} />
          <span className="max-w-[220px] truncate max-md:max-w-[120px]" title={modelTitle(ref)}>
            {modelTitle(ref)}
          </span>
          {refs.length > 2 && (
            <button aria-label={`Remove ${modelTitle(ref)}`} onClick={() => setRefs(refs.filter((_, j) => j !== i))} className="rounded p-0.5 text-faint hover:bg-hover hover:text-text">
              <X size={12} />
            </button>
          )}
          {i === 0 && refs.length === 2 && (
            <button aria-label="Swap the models" title="Swap" onClick={() => setRefs([refs[1]!, refs[0]!])} className="rounded p-0.5 text-faint hover:bg-hover hover:text-text">
              <ArrowLeftRight size={12} />
            </button>
          )}
        </span>
      ))}
      {refs.length < LETTERS.length && <AddModel refs={refs} onAdd={(r) => setRefs([...refs, r])} />}
      <div className="ml-auto flex rounded-md bg-panel-2 p-0.5" role="radiogroup" aria-label="Comparison view">
        {(
          [
            ['overlay', 'Overlay'],
            ['side', 'Side by side'],
            ['matrix', 'Matrix'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            role="radio"
            aria-checked={mode === value}
            disabled={value !== 'matrix' && refs.length > 2}
            onClick={() => setMode(value)}
            className={cn('h-6 rounded px-2 text-[12px] disabled:opacity-35 pointer-coarse:h-8', mode === value ? 'bg-active text-text' : 'text-muted hover:text-text')}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (!ready) {
    const error = loadErrors.find(Boolean);
    return (
      <div className="flex h-full flex-col" data-testid="compare">
        {header}
        <div className="flex flex-1 items-center justify-center gap-2 text-muted">{error ?? <><Loader2 size={16} className="animate-spin" /> Reading models…</>}</div>
      </div>
    );
  }

  const diff = diffs[1];
  const changeTools = (
    <>
      <Divider />
      <ToolButton label={showChanges ? 'Hide the list of differences' : 'Show the list of differences'} active={showChanges} onClick={() => setShowChanges(!showChanges)}>
        {showChanges ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
      </ToolButton>
    </>
  );
  const status = (graph: Graph, letter: string) => (
    <>
      {letter} · {graph.nodes.filter((n) => n.diff !== 'removed').length} entities
    </>
  );

  let body: React.ReactNode;
  if (mode === 'matrix' || refs.length > 2) {
    body = <Matrix refs={refs} models={sides.map((s) => s.model)} loaded={loaded as LoadedModel[]} diffs={diffs} />;
  } else if (!graphs || !positions) {
    body = (
      <div className="flex flex-1 items-center justify-center gap-2 text-muted">
        <Loader2 size={16} className="animate-spin" /> Arranging…
      </div>
    );
  } else if (mode === 'overlay') {
    body = (
      <div className="relative min-w-0 flex-1">
        <Canvas
          graph={graphs.overlay}
          positions={positions}
          selected={selectedFor(graphs.overlay)}
          onSelect={onSelectIn('overlay')}
          onMoved={save}
          onRelayout={() => void relayout()}
          fitKey={`compare:${refs.map(refKey).join('+')}`}
          reveal={null}
          status={<>A → B · {diff?.changes.length ?? 0} differences</>}
          tools={changeTools}
        />
      </div>
    );
  } else {
    body = (
      <div className="flex min-w-0 flex-1 @max-[40rem]:flex-col">
        {([graphs.left, graphs.right] as const).map((graph, i) => (
          <div key={i} className={cn('relative min-h-0 min-w-0 flex-1', i === 1 && 'border-l border-border @max-[40rem]:border-t @max-[40rem]:border-l-0')}>
            <Canvas
              graph={graph}
              positions={positions}
              selected={selectedFor(graph)}
              onSelect={onSelectIn(i as 0 | 1)}
              onMoved={save}
              onRelayout={() => void relayout()}
              fitKey={`compare-side:${refs.map(refKey).join('+')}`}
              reveal={null}
              status={status(graph, LETTERS[i]!)}
              tools={i === 0 ? changeTools : undefined}
              bare={i === 1}
              viewport={viewport}
              onViewport={setViewport}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="@container flex h-full flex-col" data-testid="compare">
      {header}
      <div className="flex min-h-0 flex-1 @max-[40rem]:flex-col">
        {body}
        {mode !== 'matrix' && refs.length === 2 && showChanges && diff && (
          <aside
            className="w-[300px] flex-none overflow-y-auto border-l border-border bg-panel @max-[40rem]:h-[42%] @max-[40rem]:w-auto @max-[40rem]:border-t @max-[40rem]:border-l-0"
            aria-label="Differences"
          >
            <p className="px-3 pt-3 text-[11px] font-semibold tracking-wider text-faint uppercase">
              From A to B · {diff.changes.length} difference{diff.changes.length === 1 ? '' : 's'}
            </p>
            <ChangeList
              changes={diff.changes}
              after={sides[1]!.model}
              before={sides[0]!.model}
              empty="The two models are the same."
              onReveal={(key, removed) => revealEntity(removed ? refs[0]! : refs[1]!, key)}
            />
          </aside>
        )}
      </div>
    </div>
  );
}

function Cell({ cell, onClick }: { cell: MatrixCell | null; onClick?: () => void }) {
  if (!cell) return <span className="text-faint">—</span>;
  return (
    <button onClick={onClick} className={cn('flex w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left hover:bg-hover', cell.state === 'added' && 'text-added', cell.state === 'changed' && 'text-changed')}>
      <span className="truncate">{cell.state === 'added' ? `new · ${cell.key}` : cell.note ? `${cell.key} (${cell.note})` : cell.state === 'changed' ? `${cell.changes} change${cell.changes === 1 ? '' : 's'}` : 'same'}</span>
      <span className="ml-auto flex-none text-[11px] text-faint">{cell.properties}</span>
    </button>
  );
}

function Matrix({ refs, models, loaded, diffs }: { refs: ModelRef[]; models: ModelSpec[]; loaded: LoadedModel[]; diffs: (ReturnType<typeof compareTwo> | null)[] }) {
  const { rows, summary } = useMemo(() => compareMatrix(models, diffs), [models, diffs]);
  const reviews = useMemo(() => models.map((m) => lintModel(m).filter((f) => f.severity === 'warning').length), [models]);
  const errors = useMemo(() => loaded.map((l) => validateDocument(l.result.doc).issues.filter((i) => i.severity === 'error').length), [loaded]);
  const summaryRows: [string, (i: number) => React.ReactNode][] = [
    ['Entities', (i) => summary[i]!.entities],
    ['Relationships', (i) => summary[i]!.relationships],
    ['Properties', (i) => summary[i]!.properties],
    ['Differences from A', (i) => (i === 0 ? '—' : summary[i]!.changes)],
    ['Review warnings', (i) => <span className={cn(reviews[i]! > 0 && 'text-changed')}>{reviews[i]}</span>],
    ['Errors', (i) => <span className={cn(errors[i]! > 0 && 'text-removed')}>{errors[i]}</span>],
  ];
  return (
    <div className="min-w-0 flex-1 overflow-auto" data-testid="compare-matrix">
      <table className="w-full border-separate border-spacing-0 text-[12.5px]">
        <thead className="sticky top-0 z-10 bg-panel">
          <tr>
            <th className="border-b border-border px-3 py-2 text-left font-medium text-muted">Entity</th>
            {refs.map((ref, i) => (
              <th key={refKey(ref)} className="min-w-[160px] border-b border-l border-border px-3 py-2 text-left font-medium">
                <span className="flex items-center gap-1.5">
                  <Letter index={i} /> <span className="truncate">{modelTitle(ref)}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summaryRows.map(([label, value]) => (
            <tr key={label} className="bg-panel-2/50">
              <td className="border-b border-border px-3 py-1 text-muted">{label}</td>
              {refs.map((ref, i) => (
                <td key={refKey(ref)} className="border-b border-l border-border px-3 py-1 tabular-nums">
                  {value(i)}
                </td>
              ))}
            </tr>
          ))}
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="border-b border-border px-3 py-0.5 font-medium">{row.id.startsWith('+') ? <span className="text-added">{row.id.slice(1)}</span> : row.id}</td>
              {row.cells.map((cell, i) => (
                <td key={i} className="border-b border-l border-border px-1.5 py-0.5">
                  <Cell cell={cell} onClick={cell ? () => revealEntity(refs[i]!, cell.key) : undefined} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
